import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PortalProductsService } from '../../../../services/portal-products.service';
import { AccountService } from '../../../../services/account.service';
import { ScrollToTop } from '../../../../utils/functions';
import { GetPortalProductFromKey } from '../../../../data/portal-products';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-fixed-income',
  templateUrl: './fixed-income.component.html',
  styleUrls: ['./fixed-income.component.scss'],
})
export class FixedIncomeComponent implements OnInit {
  product;
  isMobileLayout;
  currentUser;
  productStatusInProgress;
  activeTab: string = 'resident'; // 'resident' or 'non-resident'
  constructor(
    private route: ActivatedRoute,
    private portalProductsService: PortalProductsService,
    private router: Router,
    private accountService: AccountService,
    private cdRef: ChangeDetectorRef,
    public translate: TranslateService
  ) {
    this.isMobileLayout = window.innerWidth <= 991;
  }

  async ngOnInit() {
    try {
      // console.log(this.product);
      this.currentUser = await this.accountService.currentUserAsync();
    } catch (e) { }
    this.loadProduct('fixed-income');
    try {
      this.productStatusInProgress = true;
      this.cdRef.detectChanges();
      await this.portalProductsService.mergeProductStatus();
      // Reload product after merging status to get updated status
      this.loadProduct('fixed-income');
      this.productStatusInProgress = false;
      this.cdRef.detectChanges();
    } catch (e) {
      this.productStatusInProgress = false;
      this.cdRef.detectChanges();
    }
    ScrollToTop();
  }

  loadProduct(productKey) {
    this.product = GetPortalProductFromKey(productKey);
  }

  async changeStatusToModifying(productId) {
    try {
      await this.portalProductsService.userChangeApprovalPendingToModifying(productId);
      await this.portalProductsService.getProductStatuses();
      this.product.status = 'Modifying';
    } catch (e) { }
  }

  goToProduct() {
    console.log(this.product);
    this.router.navigate([`/${this.product.key}/signup`]);
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  goBack() {
    this.router.navigate(['/dashboard']);
  }

  getStatusClass(status: string): string {
    const statusMap = {
      'Draft': 'status-draft',
      'Submitted': 'status-submitted',
      'Re-Submitted': 'status-resubmitted',
      'Approval Pending': 'status-pending',
      'Returned': 'status-returned',
      'Rejected': 'status-rejected',
      'Approved': 'status-approved',
      'Modifying': 'status-modifying',
      'Awaiting CDS Approval': 'status-awaiting'
    };
    return statusMap[status] || 'status-default';
  }

  getStatusText(status: string): string {
    const isBengali = this.translate.currentLang === 'bn' || this.translate.currentLang === 'bn-BD';
    const statusMap = {
      'Draft': isBengali ? 'খসড়া' : 'Draft',
      'Submitted': isBengali ? 'জমা দেওয়া হয়েছে' : 'Submitted',
      'Re-Submitted': isBengali ? 'পুনরায় জমা দেওয়া হয়েছে' : 'Re-Submitted',
      'Approval Pending': isBengali ? 'অনুমোদন মুলতুবি' : 'Approval Pending',
      'Returned': isBengali ? 'ফেরত দেওয়া হয়েছে' : 'Returned',
      'Rejected': isBengali ? 'প্রত্যাখ্যান' : 'Rejected',
      'Approved': isBengali ? 'অনুমোদিত' : 'Approved',
      'Modifying': isBengali ? 'সম্পাদনা করা হচ্ছে' : 'Modifying',
      'Awaiting CDS Approval': isBengali ? 'সিডিএস অনুমোদনের অপেক্ষায়' : 'Awaiting CDS Approval'
    };
    return statusMap[status] || status;
  }

  getStatusDescription(status: string): string {
    const isBengali = this.translate.currentLang === 'bn' || this.translate.currentLang === 'bn-BD';
    const descriptionMap = {
      'Draft': isBengali ? 'আবেদন সম্পূর্ণ হয়নি' : 'Application incomplete',
      'Submitted': isBengali ? 'জমা দেওয়া হয়েছে' : 'Submitted successfully',
      'Re-Submitted': isBengali ? 'পুনরায় জমা দেওয়া হয়েছে' : 'Resubmitted',
      'Approval Pending': isBengali ? 'পর্যালোচনা চলছে' : 'Under review',
      'Returned': isBengali ? 'ফেরত দেওয়া হয়েছে' : 'Returned for correction',
      'Rejected': isBengali ? 'প্রত্যাখ্যান করা হয়েছে' : 'Application rejected',
      'Approved': isBengali ? 'অনুমোদিত হয়েছে' : 'Application approved',
      'Modifying': isBengali ? 'সম্পাদনা চলছে' : 'Being modified',
      'Awaiting CDS Approval': isBengali ? 'সিডিএস অনুমোদনের অপেক্ষায়' : 'Awaiting CDS approval'
    };
    return descriptionMap[status] || '';
  }

  getProductDescription(status: string): string {
    const isBengali = this.translate.currentLang === 'bn' || this.translate.currentLang === 'bn-BD';

    // Default description for minimum investment
    const defaultDesc = isBengali
      ? 'অনুগ্রহ করে মনে রাখবেন যে CAL-এর মাধ্যমে ফিক্সড ইনকাম অ্যাকাউন্ট খোলার জন্য ন্যূনতম বিনিয়োগ সীমা ১,০০,০০০/- টাকা'
      : 'Please note that there is a minimum investment limit of Rs. 1,000,000/- in order to open a Fixed Income account through CAL';

    // Status-specific descriptions
    const statusDescriptionMap = {
      'Draft': isBengali
        ? 'আপনার আবেদন সম্পূর্ণ করুন এবং জমা দিন। ন্যূনতম বিনিয়োগ সীমা ১,০০,০০০/- টাকা।'
        : 'Complete your application and submit. Minimum investment limit is Rs. 1,000,000/-.',
      'Submitted': isBengali
        ? 'আপনার আবেদন সফলভাবে জমা দেওয়া হয়েছে। পর্যালোচনা প্রক্রিয়া চলছে।'
        : 'Your application has been submitted successfully. Review process is in progress.',
      'Re-Submitted': isBengali
        ? 'আপনার আবেদন পুনরায় জমা দেওয়া হয়েছে। পর্যালোচনা চলছে।'
        : 'Your application has been resubmitted. Review is in progress.',
      'Approval Pending': isBengali
        ? 'আপনার আবেদন পর্যালোচনার জন্য অপেক্ষা করছে। আমরা শীঘ্রই আপনাকে জানাব।'
        : 'Your application is pending review. We will notify you shortly.',
      'Returned': isBengali
        ? 'আপনার আবেদন সংশোধনের জন্য ফেরত দেওয়া হয়েছে। অনুগ্রহ করে প্রয়োজনীয় পরিবর্তন করুন এবং পুনরায় জমা দিন।'
        : 'Your application has been returned for correction. Please make necessary changes and resubmit.',
      'Rejected': isBengali
        ? 'দুঃখিত, আপনার আবেদন প্রত্যাখ্যান করা হয়েছে। আরও তথ্যের জন্য আমাদের সাথে যোগাযোগ করুন।'
        : 'Sorry, your application has been rejected. Please contact us for more information.',
      'Approved': isBengali
        ? 'অভিনন্দন! আপনার আবেদন অনুমোদিত হয়েছে। আপনি এখন বিনিয়োগ শুরু করতে পারেন।'
        : 'Congratulations! Your application has been approved. You can now start investing.',
      'Modifying': isBengali
        ? 'আপনার আবেদন সম্পাদনা করা হচ্ছে। সম্পাদনা সম্পন্ন হলে আপনি জমা দিতে পারবেন।'
        : 'Your application is being modified. You can submit once editing is complete.',
      'Awaiting CDS Approval': isBengali
        ? 'আপনার আবেদন সিডিএস অনুমোদনের জন্য অপেক্ষা করছে।'
        : 'Your application is awaiting CDS approval.'
    };

    return statusDescriptionMap[status] || defaultDesc;
  }
}
